import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FOAM_DETAILS = {
  '35-200': { firmness: 'Firm', idealFor: 'Dining chairs, window seats, high-traffic areas', lifespan: '8-10 years', feel: 'Supportive and resilient. Springs back quickly after use. Excellent for areas needing structural support.' },
  '30-130': { firmness: 'Medium-Firm', idealFor: 'Lounge seating, day beds, reading nooks', lifespan: '6-8 years', feel: 'A great balance between comfort and support. Slightly softer than 35-200 while maintaining shape over time.' },
  '23-130': { firmness: 'Medium', idealFor: 'Sofa cushions, bedroom seating, cushioned benches', lifespan: '5-7 years', feel: 'Soft and inviting. Provides a gentle sit with moderate support. Perfect for relaxed, everyday use.' },
  '17-070': { firmness: 'Soft', idealFor: 'Back cushions, scatter cushions, decorative bolsters', lifespan: '3-5 years', feel: 'Very soft and plush. Excellent for back support and comfort layers. Not recommended as a sole seat foam.' },
};

const FIRMNESS_COLORS = {
  Firm: 'bg-amber-100 text-amber-800',
  'Medium-Firm': 'bg-yellow-100 text-yellow-800',
  Medium: 'bg-green-100 text-green-800',
  Soft: 'bg-blue-100 text-blue-800',
};

const OurFoamsPage = () => {
  const [foams, setFoams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/foam-types`)
      .then(r => r.json())
      .then(data => { setFoams(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-[#FAFAF8]" data-testid="our-foams-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Our Foams</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-3xl font-body">
            We use premium Australian-made foams across all our cushions. Each density and firmness is suited to different applications. Understanding foam grades helps you choose the right comfort level for your project.
          </p>
        </div>
      </section>

      <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12 py-6 sm:py-12">
        {/* How foam grades work */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm mb-10">
          <h2 className="text-xl sm:text-2xl font-display font-semibold text-[#1A1A1A] mb-4">Understanding Foam Grades</h2>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Foam is measured by two key numbers: <strong>density</strong> (kg/m3) and <strong>hardness</strong> (Newtons). For example, our 35-200 foam has a density of 35 kg/m3 and a hardness of 200 Newtons. Higher density means the foam will last longer and retain its shape better. Higher hardness means a firmer feel.
          </p>
          <p className="text-gray-600 leading-relaxed">
            The right foam depends on how the cushion will be used. A dining chair needs firm support, while a sofa back cushion benefits from a softer feel. We recommend choosing based on your primary use case below.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-7 w-7 animate-spin text-teal-500" />
          </div>
        ) : (
          <div className="grid gap-6">
            {foams.map(foam => {
              const details = FOAM_DETAILS[foam.id] || {};
              const colorClass = FIRMNESS_COLORS[details.firmness] || 'bg-gray-100 text-gray-800';
              return (
                <div key={foam.id} className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm" data-testid={`foam-${foam.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-lg sm:text-xl font-display font-semibold text-[#1A1A1A]">{foam.name}</h3>
                        {details.firmness && (
                          <span className={`px-3 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>{details.firmness}</span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-4 leading-relaxed">{foam.description}</p>
                      {details.feel && (
                        <p className="text-gray-500 text-sm mb-4 italic">"{details.feel}"</p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        {details.idealFor && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-gray-400 text-xs font-medium mb-1">Ideal For</p>
                            <p className="text-gray-700">{details.idealFor}</p>
                          </div>
                        )}
                        {details.lifespan && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-gray-400 text-xs font-medium mb-1">Expected Lifespan</p>
                            <p className="text-gray-700">{details.lifespan}</p>
                          </div>
                        )}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-400 text-xs font-medium mb-1">Available Thicknesses</p>
                          <p className="text-gray-700">{foam.available_thicknesses?.map(t => `${t}cm`).join(', ') || 'Various'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OurFoamsPage;
